import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw01 } from "@untitledui/icons";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useServerStats, type LatencyBucket } from "@/lib/stats";
import { formatBytes, formatTime, formatDateTime } from "@/lib/format";
import { AppSelect } from "@/components/app/AppSelect";
import { formatRemaining, usePerformanceMode } from "@/lib/performanceMode";

function Metric({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-xl bg-primary p-4 ring-1 ring-secondary">
      <p className="text-sm font-medium text-tertiary">{label}</p>
      <p className="mt-1.5 text-display-xs font-semibold text-primary">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-tertiary">{sub}</p>}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="mb-4 text-sm font-semibold text-secondary">{title}</h2>
      {children}
    </div>
  );
}

/** Progress bar 0–100 % s barvou dle vytížení. */
function Bar({ pct }: { pct: number }) {
  const p = Math.max(0, Math.min(100, pct));
  const color = p >= 90 ? "bg-error-solid" : p >= 70 ? "bg-warning-solid" : "bg-success-solid";
  return (
    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${p}%` }} />
    </div>
  );
}

function Row({ k, v }: { k: string; v: string | number }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-tertiary">{k}</dt>
      <dd className="font-medium text-primary">{v}</dd>
    </div>
  );
}

function LatencyCard({ title, b }: { title: string; b: LatencyBucket }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl bg-primary p-4 ring-1 ring-secondary">
      <h3 className="mb-3 text-sm font-semibold text-secondary">{title}</h3>
      <div className="grid grid-cols-3 gap-2 text-center">
        {(["p50", "p95", "p99"] as const).map((p) => (
          <div key={p} className="rounded-lg bg-secondary py-2 ring-1 ring-secondary">
            <div className="text-xs text-tertiary uppercase">{p}</div>
            <div className="text-lg font-semibold text-primary">{b[p]}<span className="text-xs text-tertiary"> ms</span></div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-tertiary">
        {t("stats.minMaxAvg")}: {b.min} / {b.max} / {b.avg.toFixed(0)} ms · {b.count}×
      </p>
    </div>
  );
}

function statusBadgeColor(code: number): "success" | "blue" | "warning" | "error" | "gray" {
  if (code >= 500) return "error";
  if (code >= 400) return "warning";
  if (code >= 300) return "blue";
  if (code >= 200) return "success";
  return "gray";
}

/**
 * Server Statistics — živé metriky z `/admin/api/v1/stats` (refetch 5 s): throughput
 * (+ graf), latency percentily (total/connector/processing), DB/HTTP pooly s progress
 * bary, JVM (memory/CPU/threads/GC), chybovost + poslední chyby.
 */
export function ServerStatsPage() {
  const { t } = useTranslation();
  const [refreshMs, setRefreshMs] = useState(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("etts.serverStats.refreshMs") : null;
    const n = saved != null ? parseInt(saved, 10) : NaN;
    return [0, 2000, 5000, 10000, 30000].includes(n) ? n : 5000;
  });
  const setRefresh = (ms: number) => {
    setRefreshMs(ms);
    if (typeof window !== "undefined") window.localStorage.setItem("etts.serverStats.refreshMs", String(ms));
  };
  const { data, isLoading, isError, isFetching, refetch, dataUpdatedAt } = useServerStats(refreshMs);

  const chartData = useMemo(
    () => (data?.throughput.history ?? []).map((h) => ({ time: formatTime(h.timestamp), rps: h.requestsPerSecond })),
    [data],
  );

  if (isLoading) return <LoadingIndicator />;
  if (isError || !data) return <p className="text-sm text-error-primary">{t("common.loadError")}</p>;

  const { throughput, latency, pools, errors, jvm } = data;
  const recent = errors.recentErrors ?? [];
  const byStatus = errors.byStatusCode ?? {};

  return (
    <div className="flex flex-col gap-6">
      <PerformanceModeNotice refreshMs={refreshMs} onSpeedUp={() => setRefresh(2000)} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-display-sm font-semibold text-primary">{t("stats.title")}</h1>
          <p className="mt-1 text-md text-tertiary">{t("stats.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {dataUpdatedAt > 0 && (
            <span className="text-xs text-tertiary">{t("stats.lastUpdate")}: {formatTime(new Date(dataUpdatedAt).toISOString())}</span>
          )}
          <AppSelect fullWidth={false} value={refreshMs} onChange={(e) => setRefresh(parseInt(e.target.value, 10))}>
            <option value={2000}>2s</option>
            <option value={5000}>5s</option>
            <option value={10000}>10s</option>
            <option value={30000}>30s</option>
            <option value={0}>{t("stats.manual")}</option>
          </AppSelect>
          <Button size="sm" color="secondary" iconLeading={RefreshCw01} isDisabled={isFetching} onPress={() => void refetch()}>
            {t("stats.refreshNow")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric label={t("stats.rps")} value={throughput.requestsPerSecond.toFixed(1)} sub={`${throughput.totalRequests} ${t("stats.totalReq")}`} />
        <Metric label={t("stats.errorRate")} value={`${errors.errorRatePercent.toFixed(1)} %`} sub={`${errors.totalErrors} / ${errors.totalSuccess + errors.totalErrors}`} />
        <Metric label="Latency p50 / p95 / p99" value={`${latency.total.p50} / ${latency.total.p95} / ${latency.total.p99} ms`} sub={`avg ${latency.total.avg.toFixed(0)} ms`} />
        <Metric label={t("stats.heap")} value={formatBytes(jvm.memory.heapUsed)} sub={`${jvm.memory.heapUtilizationPercent.toFixed(1)} % z ${formatBytes(jvm.memory.heapMax)}`} />
      </div>

      <Panel title={t("stats.throughput")}>
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Metric label={t("stats.rps")} value={throughput.requestsPerSecond.toFixed(1)} />
          <Metric label={t("stats.last1m")} value={throughput.last1m} />
          <Metric label={t("stats.last5m")} value={throughput.last5m} />
          <Metric label={t("stats.last1h")} value={throughput.last1h} />
          <Metric label={t("stats.totalReq")} value={throughput.totalRequests} />
        </div>
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="rps" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-brand-600)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--color-brand-600)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="time" tick={{ fontSize: 11, fill: "var(--color-text-tertiary)" }} interval="preserveStartEnd" minTickGap={40} />
              <YAxis tick={{ fontSize: 11, fill: "var(--color-text-tertiary)" }} width={40} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--color-border-secondary)", background: "var(--color-bg-primary)" }}
                labelStyle={{ color: "var(--color-text-secondary)" }}
              />
              <Area type="monotone" dataKey="rps" stroke="var(--color-brand-600)" strokeWidth={2} fill="url(#rps)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      {/* Latency percentily */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-secondary">{t("stats.latency")}</h2>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <LatencyCard title={t("stats.latencyTotal")} b={latency.total} />
          <LatencyCard title={t("stats.latencyConnector")} b={latency.connector} />
          <LatencyCard title={t("stats.latencyProcessing")} b={latency.processing} />
        </div>
      </div>

      {/* Pooly + JVM */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel title={t("stats.dbPool")}>
          <div className="mb-3">
            <div className="flex justify-between text-sm">
              <span className="text-tertiary">{t("stats.utilization")}</span>
              <span className="font-medium text-primary">{pools.database.utilizationPercent.toFixed(1)} %</span>
            </div>
            <Bar pct={pools.database.utilizationPercent} />
          </div>
          <dl className="flex flex-col gap-2 text-sm">
            <Row k={t("stats.active")} v={`${pools.database.active} / ${pools.database.total}`} />
            <Row k={t("stats.idle")} v={pools.database.idle} />
            <Row k={t("stats.waiting")} v={pools.database.waiting} />
            <Row k="Max" v={pools.database.max} />
          </dl>
        </Panel>
        <Panel title={t("stats.httpPool")}>
          <div className="mb-3">
            <div className="flex justify-between text-sm">
              <span className="text-tertiary">{t("stats.utilization")}</span>
              <span className="font-medium text-primary">{pools.http.utilizationPercent.toFixed(1)} %</span>
            </div>
            <Bar pct={pools.http.utilizationPercent} />
          </div>
          <dl className="flex flex-col gap-2 text-sm">
            <Row k={t("stats.leased")} v={`${pools.http.leased} / ${pools.http.max}`} />
            <Row k={t("stats.available")} v={pools.http.available} />
            <Row k={t("stats.pending")} v={pools.http.pending} />
          </dl>
        </Panel>
        <Panel title={t("stats.jvm")}>
          <div className="mb-2">
            <div className="flex justify-between text-sm">
              <span className="text-tertiary">{t("stats.heap")}</span>
              <span className="font-medium text-primary">{jvm.memory.heapUtilizationPercent.toFixed(1)} %</span>
            </div>
            <Bar pct={jvm.memory.heapUtilizationPercent} />
          </div>
          <div className="mb-2">
            <div className="flex justify-between text-sm">
              <span className="text-tertiary">{t("stats.processCpu")}</span>
              <span className="font-medium text-primary">{(jvm.cpu.processCpuUsage * 100).toFixed(0)} %</span>
            </div>
            <Bar pct={jvm.cpu.processCpuUsage * 100} />
          </div>
          <div className="mb-3">
            <div className="flex justify-between text-sm">
              <span className="text-tertiary">{t("stats.systemCpu")}</span>
              <span className="font-medium text-primary">{(jvm.cpu.systemCpuUsage * 100).toFixed(0)} %</span>
            </div>
            <Bar pct={jvm.cpu.systemCpuUsage * 100} />
          </div>
          <dl className="flex flex-col gap-2 text-sm">
            <Row k={t("stats.nonHeap")} v={formatBytes(jvm.memory.nonHeapUsed)} />
            <Row k={t("stats.threads")} v={`${jvm.threads.live} (${jvm.threads.daemon} daemon, peak ${jvm.threads.peak})`} />
            <Row k="GC" v={`${jvm.gc.pauseCount}× / ${jvm.gc.pauseTotalMs} ms`} />
            <Row k={t("stats.gcLastPause")} v={`${jvm.gc.lastPauseMs} ms`} />
            <Row k="CPU cores" v={jvm.cpu.availableProcessors} />
          </dl>
        </Panel>
      </div>

      {/* Chyby */}
      <Panel title={t("stats.errors")}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-tertiary">{t("stats.errorRate")}:</span>
          <span className="text-sm font-medium text-primary">{errors.errorRatePercent.toFixed(1)} %</span>
          {Object.entries(byStatus).map(([code, count]) => (
            <Badge key={code} type="pill-color" size="sm" color={statusBadgeColor(Number(code))}>
              {code}: {count}
            </Badge>
          ))}
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-tertiary">{t("stats.noErrors")}</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold text-tertiary">{t("stats.recentErrors")}</p>
            {recent.slice(0, 10).map((e, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg bg-secondary px-3 py-1.5 text-sm ring-1 ring-secondary">
                <span className="text-tertiary">{formatDateTime(e.timestamp)}</span>
                {e.statusCode != null && (
                  <Badge type="pill-color" size="sm" color={statusBadgeColor(e.statusCode)}>{e.statusCode}</Badge>
                )}
                <span className="font-mono text-xs text-secondary">{e.endpoint ?? "—"}</span>
                {e.errorMessage && <span className="text-error-primary">{e.errorMessage}</span>}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

/**
 * Upozornění, že běží režim Performance, plus nabídka zrychlit obnovování na 2 s
 * (přesně TTL cache `serverStats` na serveru — kratší interval by jen vracel totéž).
 *
 * Uživatelovu volbu uloženou v `localStorage` **nepřepisuje sama** — zrychlení je
 * tlačítko, ne vedlejší účinek zapnutí režimu.
 */
function PerformanceModeNotice({ refreshMs, onSpeedUp }: { refreshMs: number; onSpeedUp: () => void }) {
  const { t } = useTranslation();
  const { data } = usePerformanceMode();
  const [remaining, setRemaining] = useState(0);

  const expiresAt = data?.active ? data.expiresAtEpochMs : 0;
  useEffect(() => {
    if (!expiresAt) return;
    setRemaining(expiresAt - Date.now());
    const id = window.setInterval(() => setRemaining(expiresAt - Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  if (!data?.active) {
    return null;
  }

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-warning-primary p-3 text-sm text-warning-primary ring-1 ring-warning"
    >
      <span>{t("perfMode.statsBanner", { remaining: formatRemaining(remaining) })}</span>
      {refreshMs !== 2000 && (
        <Button size="sm" color="secondary" onPress={onSpeedUp}>
          {t("perfMode.faster")}
        </Button>
      )}
    </div>
  );
}
